import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Plus,
  X,
} from 'lucide-react';
import {
  getRevisions,
  submitRevisionBatch,
  Revision,
  RevisionItem,
  RevisionCategory,
  RevisionLocation,
  categoryLabels,
  locationLabels,
} from '../api/website';
import { PortalFooter } from '../components/PortalFooter';

interface FormItem {
  id: string;
  category: RevisionCategory;
  location: RevisionLocation;
  description: string;
}

const createEmptyItem = (): FormItem => ({
  id: `item_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
  category: 'text',
  location: 'general',
  description: '',
});

export default function RevisionsPage() {
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state - array of items
  const [items, setItems] = useState<FormItem[]>([createEmptyItem()]);

  useEffect(() => {
    fetchRevisions();
  }, []);

  async function fetchRevisions() {
    try {
      const { revisions } = await getRevisions();
      setRevisions(revisions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load revisions');
    } finally {
      setIsLoading(false);
    }
  }

  const addItem = () => {
    if (items.length >= 10) return;
    setItems([...items, createEmptyItem()]);
  };

  const removeItem = (id: string) => {
    if (items.length <= 1) return;
    setItems(items.filter(item => item.id !== id));
  };

  const updateItem = (id: string, field: keyof FormItem, value: string) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate all items have descriptions
    const invalidItem = items.find(item => !item.description.trim());
    if (invalidItem) {
      setError('Please fill in a description for each change request');
      return;
    }

    setIsSubmitting(true);

    try {
      const revisionItems: RevisionItem[] = items.map(item => ({
        category: item.category,
        location: item.location,
        description: item.description.trim(),
      }));

      await submitRevisionBatch(revisionItems);

      setSuccess(`${items.length} change request${items.length > 1 ? 's' : ''} submitted. We'll get to work on ${items.length > 1 ? 'them' : 'it'} right away!`);
      setItems([createEmptyItem()]);

      // Refresh revisions list
      fetchRevisions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit revisions');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'processing':
        return 'In Progress';
      case 'failed':
        return 'Failed';
      case 'rejected':
        // Never let this fall through to the default — a declined request
        // showing as "Pending" would leave the customer waiting for something
        // that is not coming.
        return 'Not being made';
      default:
        return 'Pending';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Request Changes</h1>
          <p className="text-gray-600 mt-1">
            Tell us what you'd like to change about your website. Add as many requests as you need.
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>{success}</div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>
        )}

        {/* Revision Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">New Change Requests</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {items.map((item, index) => (
              <div
                key={item.id}
                className="border border-gray-200 rounded-lg p-4 relative"
              >
                {/* Item number and remove button */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-500">
                    Change #{index + 1}
                  </span>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove this item"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  {/* Category dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      What type of change?
                    </label>
                    <select
                      value={item.category}
                      onChange={(e) => updateItem(item.id, 'category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {(Object.keys(categoryLabels) as RevisionCategory[]).map(key => (
                        <option key={key} value={key}>
                          {categoryLabels[key]}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Location dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Where on the page?
                    </label>
                    <select
                      value={item.location}
                      onChange={(e) => updateItem(item.id, 'location', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {(Object.keys(locationLabels) as RevisionLocation[]).map(key => (
                        <option key={key} value={key}>
                          {locationLabels[key]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    What would you like changed? *
                  </label>
                  <textarea
                    value={item.description}
                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                    rows={3}
                    placeholder="Be as specific as possible - include exact text, colors, or describe what you'd like to see..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    required
                  />
                </div>
              </div>
            ))}

            {/* Add another item button */}
            {items.length < 10 && (
              <button
                type="button"
                onClick={addItem}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Another Change
              </button>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting || items.every(item => !item.description.trim())}
              className="w-full sm:w-auto px-6 py-3 bg-accent-500 hover:bg-accent-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit {items.length} Request{items.length > 1 ? 's' : ''}
                </>
              )}
            </button>
          </form>
        </div>

        {/* Revision History */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Revision History</h2>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
            </div>
          ) : revisions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No revision requests yet.
            </div>
          ) : (
            <div className="space-y-4">
              {revisions.map((revision) => (
                <div
                  key={revision.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(revision.status)}
                      <span className="text-sm font-medium text-gray-900">
                        {getStatusLabel(revision.status)}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(revision.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  {revision.staff_note && (
                    <div className={`text-sm rounded-lg p-3 mb-2 ${
                      revision.status === 'rejected'
                        ? 'bg-gray-50 border border-gray-200 text-gray-700'
                        : 'bg-blue-50 border border-blue-100 text-blue-800'
                    }`}>
                      <span className="font-medium">
                        {revision.status === 'rejected' ? 'Why: ' : 'Update: '}
                      </span>
                      {revision.staff_note}
                    </div>
                  )}

                  {(revision.page || revision.section) && (
                    <div className="text-sm text-gray-600 mb-2">
                      {revision.page && (
                        <span className="inline-flex px-2 py-0.5 bg-gray-100 rounded text-gray-700 mr-2">
                          {revision.page}
                        </span>
                      )}
                      {revision.section && (
                        <span className="inline-flex px-2 py-0.5 bg-gray-100 rounded text-gray-700">
                          {revision.section}
                        </span>
                      )}
                    </div>
                  )}

                  <p className="text-gray-700 whitespace-pre-wrap">{revision.description}</p>

                  {revision.processed_at && (
                    <p className="text-sm text-gray-500 mt-2">
                      Completed: {new Date(revision.processed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <PortalFooter />
    </div>
  );
}
